import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useComparisonContext } from '@/contexts/ComparisonContext';
import { GitCompare, X } from 'lucide-react';
import { Compare } from '@/components/Compare';

export const FloatingActions = () => {
  const { comparisonList, removeFromComparison, clearComparison } = useComparisonContext();
  const [showComparison, setShowComparison] = useState(false);

  const handleCompareClick = () => {
    if (comparisonList.length === 0) return;
    setShowComparison(true);
  };

  if (showComparison) {
    return (
      <div className="fixed inset-0 bg-background z-50 overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className="text-2xl font-bold">Compare AI Bots</h1>
          <Button variant="ghost" onClick={() => setShowComparison(false)} aria-label="Close comparison view">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Compare
          comparisonList={comparisonList}
          onRemoveFromComparison={removeFromComparison}
          onClearComparison={clearComparison}
        />
      </div>
    );
  }

  if (comparisonList.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <div className="relative">
        <Card className="absolute bottom-16 right-0 w-64 shadow-lg mb-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Comparison</span>
              <Badge variant="secondary">{comparisonList.length}</Badge>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {comparisonList.slice(0, 3).map((bot) => (
                <div key={bot.id} className="flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{bot.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromComparison(bot.id)}
                    className="h-6 w-6 p-0"
                    aria-label={`Remove ${bot.name} from comparison`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {comparisonList.length > 3 && (
                <p className="text-xs text-muted-foreground">+{comparisonList.length - 3} more</p>
              )}
            </div>
            <Button size="sm" onClick={handleCompareClick} className="w-full mt-2">
              Compare Now
            </Button>
          </CardContent>
        </Card>
        
        <Button
          size="lg"
          onClick={handleCompareClick}
          className="h-14 w-14 rounded-full shadow-lg hover:scale-105 transition-transform relative"
          aria-label={`Compare bots (${comparisonList.length} selected)`}
        >
          <GitCompare className="h-5 w-5" />
          <Badge 
            variant="destructive" 
            className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
          >
            {comparisonList.length}
          </Badge>
        </Button>
      </div>
    </div>
  );
};
